import { useCallback, useRef, useState } from 'react';
import { TOKEN } from '@nxsys/store/constants/keys';
import { resolveAuthTarget } from '@nxsys/utils/authTarget';
import type { ChatStreamEvent } from '@nxsys/types/models/AiChat';

const buildStreamUrl = (path: string, isBureau: boolean) => {
  const baseUrl = isBureau
    ? import.meta.env.VITE_API_URL_BUREAU
    : import.meta.env.VITE_API_URL;
  return `${baseUrl}${path}`;
};

export const useAiChatStream = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (
      query: string,
      sessionId: string | undefined,
      onEvent: (event: ChatStreamEvent) => void,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsStreaming(true);

      try {
        const { authCode, isBureau } = resolveAuthTarget();

        const url = authCode
          ? buildStreamUrl(
              `/ai/chat/stream?auth_code=${encodeURIComponent(authCode)}`,
              isBureau,
            )
          : buildStreamUrl('/ai/chat/stream', isBureau);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const jwtToken = authCode ? null : localStorage.getItem(TOKEN);
        if (jwtToken) {
          headers['Authorization'] = `Bearer ${jwtToken}`;
        }
        if (isBureau) {
          headers['Accept'] = 'application/vnd.user-backend.v1';
        }

        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, sessionId }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (!res.body) throw new Error('No response body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event: ChatStreamEvent = JSON.parse(line.slice(6));
                onEvent(event);
              } catch {
                // Skip malformed JSON
              }
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          onEvent({ type: 'error', message: err.message });
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { sendMessage, isStreaming, abort };
};
